import React, { useState } from 'react';
import { AlertTriangle, Info, ChevronDown, ChevronRight } from 'lucide-react';
import { FirmaConfigurationSummary, IntegrationInfo, JobInfo, MessageInfo } from '../../types/ESentral';

interface ESentralConfigViewProps {
  config: FirmaConfigurationSummary;
}

const statusStyle: Record<IntegrationInfo['status'], string> = {
  ok: 'text-green-700',
  avvik: 'text-red-700',
  ukjent: 'text-gray-400',
};

const statusText: Record<IntegrationInfo['status'], string> = {
  ok: 'ok',
  avvik: 'avvik',
  ukjent: '',
};

const plural = (n: number, en: string, flere: string) => `${n} ${n === 1 ? en : flere}`;

const JobLine: React.FC<{ label: string; job?: JobInfo }> = ({ label, job }) => {
  if (!job) return null;
  return (
    <div className="flex gap-2">
      <span className="w-14 flex-shrink-0 text-gray-400">{label}</span>
      {job.mangler ? (
        <span className="text-red-700">{job.jobNr} finnes ikke</span>
      ) : (
        <>
          <span className="font-mono text-gray-500">{job.jobNr}</span>
          <span className="flex-1 truncate" title={job.beskrivelse}>{job.beskrivelse || '-'}</span>
          <span className="text-gray-400">{job.tidsplan}</span>
          {job.sperret && <span className="text-amber-700 font-medium">sperret</span>}
        </>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; summary?: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, summary, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-1 px-3 py-2 text-left hover:bg-gray-50">
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <span className="font-medium text-gray-900">{title}</span>
        {summary && <span className="text-gray-500 text-xs"> · {summary}</span>}
      </button>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
};

const messageFlags = (messages: MessageInfo[]): string => {
  const sperret = messages.filter(m => m.henteJobb?.sperret || m.sendeJobb?.sperret).length;
  const ikkeIDrift = messages.filter(m => m.driftsstatus !== 'i drift').length;
  return [
    plural(messages.length, 'melding', 'meldinger'),
    sperret > 0 && `${sperret} med sperret jobb`,
    ikkeIDrift > 0 && `${ikkeIDrift} ikke i drift`,
  ].filter(Boolean).join(' · ');
};

const MessageRow: React.FC<{ m: MessageInfo }> = ({ m }) => (
  <div className="px-3 py-1.5 border-t border-gray-100 first:border-t-0 text-xs text-gray-600">
    <div className="flex gap-2 items-baseline">
      <span className="font-medium text-gray-800 flex-1 truncate" title={m.beskrivelse}>{m.beskrivelse || '-'}</span>
      <span className="font-mono text-gray-400">{m.fraFormat} → {m.tilFormat}</span>
      {m.driftsstatus !== 'i drift' && <span className="text-amber-700 font-medium">{m.driftsstatus}</span>}
    </div>
    <JobLine label="Henting" job={m.henteJobb} />
    <JobLine label="Sending" job={m.sendeJobb} />
    {m.tilkoblinger.length > 0 && (
      <div className="flex gap-2">
        <span className="w-14 flex-shrink-0 text-gray-400">Tilkobl.</span>
        <span>{m.tilkoblinger.join(', ')}</span>
      </div>
    )}
  </div>
);

const integrationDetail = (i: IntegrationInfo): string => {
  const parts: string[] = [];
  if (i.serviceType === '2') {
    if (i.host) parts.push(i.host);
    if (i.urlCompanyNo) parts.push(`CompanyNo ${i.urlCompanyNo}`);
    if (i.tokenRequestCompanyNo) parts.push(`kundenr ${i.tokenRequestCompanyNo}`);
  } else {
    if (i.klientNavn) parts.push(`${i.serviceType === '6' ? 'firmanummer' : 'klient'} ${i.klientNavn}`);
    if (i.tenantId) parts.push(`TenantID ${i.tenantId}`);
    if (!i.klientNavn && i.host) parts.push(i.host);
  }
  return parts.join(' · ');
};

const usage = (i: IntegrationInfo): string => {
  if (i.jobCount === 0 && i.messageCount === 0) return 'ingen direkte kobling funnet';
  return [
    i.messageCount > 0 && plural(i.messageCount, 'melding', 'meldinger'),
    i.jobCount > 0 && plural(i.jobCount, 'jobb', 'jobber'),
  ].filter(Boolean).join(', ');
};

export const ESentralConfigView: React.FC<ESentralConfigViewProps> = ({ config }) => {
  const errors = config.issues.filter(i => i.level === 'error');
  const warnings = config.issues.filter(i => i.level === 'warning');

  const partners = Array.from(
    config.messages.reduce((map, m) => {
      const list = map.get(m.partnerId) ?? [];
      list.push(m);
      return map.set(m.partnerId, list);
    }, new Map<string, MessageInfo[]>())
  );

  return (
    <div className="space-y-3 text-sm">
      <div className="pb-2 border-b border-gray-200">
        <span className="font-semibold text-gray-900">eSentral {config.firmaNr}</span>
        <span className="text-gray-500"> · {config.navn || '-'} · KundeNr {config.kundeNr || '-'}</span>
        {config.tenantDatabase && (
          <div className="text-xs text-gray-400 mt-0.5">
            Sjekket mot {config.tenantDatabase}
            {config.tenantCompanyNos.length > 0 && ` · CompanyNo ${config.tenantCompanyNos.join(', ')}`}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 text-red-800 rounded px-3 py-2 space-y-1">
          {errors.map((issue, idx) => (
            <div key={idx} className="flex gap-2"><AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />{issue.message}</div>
          ))}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="bg-amber-50 text-amber-800 rounded px-3 py-2 space-y-1">
          {warnings.map((issue, idx) => (
            <div key={idx} className="flex gap-2"><Info className="h-4 w-4 flex-shrink-0 mt-0.5" />{issue.message}</div>
          ))}
        </div>
      )}

      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">Meldinger per utvekslingspartner</div>
        {partners.length === 0 ? (
          <div className="text-xs text-gray-400">Ingen meldinger</div>
        ) : (
          <div className="space-y-2">
            {partners.map(([partnerId, messages]) => (
              <Section key={partnerId} title={`${partnerId} ${messages[0].partnerNavn ?? ''}`} summary={messageFlags(messages)}>
                {messages.map(m => <MessageRow key={m.meldId} m={m} />)}
              </Section>
            ))}
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded px-3 py-2">
        <div className="font-medium text-gray-900 mb-1">Tilkoblinger</div>
        {config.integrations.length === 0 ? (
          <div className="text-xs text-gray-400">Ingen tilkoblinger</div>
        ) : (
          <div className="text-xs space-y-1">
            {config.integrations.map(i => (
              <div key={i.id} className="flex gap-2">
                <span className="flex-1 min-w-0 text-gray-600">
                  <span className="font-medium text-gray-800">{i.name}</span>
                  {' · '}{[i.beskrivelse, integrationDetail(i), usage(i)].filter(Boolean).join(' · ')}
                </span>
                <span className={`font-medium ${statusStyle[i.status]}`}>{statusText[i.status]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {config.otherJobs.length > 0 && (
        <Section title="Øvrige kommunikasjonsjobber" summary={`ikke brukt av noen melding · ${config.otherJobs.length} stk`}>
          <div className="px-3 py-1.5 text-xs text-gray-600">
            {config.otherJobs.map(job => <JobLine key={job.jobNr} label="" job={job} />)}
          </div>
        </Section>
      )}

      <div className="border border-gray-200 rounded px-3 py-2">
        <div className="font-medium text-gray-900 mb-1">Tenant-database (styringsfil .udl)</div>
        {config.udlFiles.length === 0 ? (
          <div className="text-xs text-gray-400">Ingen styringsfiler</div>
        ) : (
          <div className="font-mono text-xs space-y-0.5">
            {config.udlFiles.map(u => (
              <div key={u.fileName}>
                {u.matchesTenant === true && <span className="text-green-700">✓ </span>}
                {u.matchesTenant === false && <span className="text-red-700">✗ </span>}
                {u.matchesTenant == null && <span className="text-gray-400">? </span>}
                {u.fileName}
                <span className="font-sans text-gray-400"> · {[
                  u.kommInfoCount > 0 && plural(u.kommInfoCount, 'SQL-jobb', 'SQL-jobber'),
                  u.dokumentParameterCount > 0 && `${u.dokumentParameterCount} dokumentparametre`,
                ].filter(Boolean).join(', ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs text-gray-400">
        {config.messages.length} MeldInfo · {config.kommInfoCount} KommInfo · {config.dbTilkoblingCount} DBTilkoblinger · {config.dokumentParameterCount} DokumentParameter
      </div>
    </div>
  );
};
